/**
 * /app/analytics — сводная аналитика по аккаунтам и стратегиям.
 *
 * Эталонная страница Graphite-редизайна (PULSE-229): карточки аккаунтов
 * (equity с чипом PnL за окно и live-индикатором свежести), сортируемая
 * таблица стратегий (двухстрочные ячейки, WR-микробары, area-спарклайны,
 * battery-пилюли, относительное время). Периоды: 24ч / 7д / 30д.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Lock } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'

const ACCOUNT_LABELS: Record<number, { name: string; real?: boolean }> = {
  1: { name: 'Дискавери' },
  2: { name: 'Реал', real: true },
  7: { name: 'Зеркало' },
  8: { name: 'Лаборатория' },
  12: { name: 'Внешний', real: true },
}

const PERIODS = [
  { label: '24ч', hours: 24 },
  { label: '7д', hours: 168 },
  { label: '30д', hours: 720 },
]

type SortKey = 'pp' | 'usd' | 'n' | 'avg_pp' | 'last_trade'

function useAnalytics(hours: number) {
  return useQuery({
    queryKey: ['lab', 'analytics', hours],
    queryFn: async () => {
      const { data } = await api.get<any>('/lab/strategy-analytics', {
        params: { hours },
      })
      return data
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
  })
}

// ─── Форматирование ──────────────────────────────────────────────────

const fmtPp = (v: number, d = 1) =>
  `${v > 0 ? '+' : ''}${Number(v).toFixed(d).replace('-', '−')}`

const fmtUsd = (v: number) =>
  `${v < 0 ? '−' : '+'}$${Math.abs(Number(v)).toFixed(2)}`

/** Относительное время: свежее читается быстрее абсолютного. */
function fmtRel(iso: string | null): string {
  if (!iso) return '—'
  const t = new Date(iso).getTime()
  const mins = Math.round((Date.now() - t) / 60000)
  if (mins < 1) return 'только что'
  if (mins < 60) return `${mins} мин назад`
  if (mins < 24 * 60) return `${Math.round(mins / 60)} ч назад`
  return new Date(iso).toLocaleString('ru', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

function fmtAge(sec: number | null): string | null {
  if (sec == null || sec < 120) return null
  if (sec < 3600) return `${Math.round(sec / 60)}м`
  return `${(sec / 3600).toFixed(1)}ч`
}

function parseLock(lock: string | null): string | null {
  if (!lock) return null
  try {
    const v = JSON.parse(lock)
    return Array.isArray(v) ? v.join(', ') : String(v)
  } catch {
    return lock
  }
}

// ─── Спарклайн: линия + заливка до нуля + endpoint-точка ─────────────

function Spark({
  values, w = 104, h = 30,
}: { values: number[]; w?: number; h?: number }) {
  if (!values || values.length < 2) {
    return <span className="text-textFaint text-2xs">—</span>
  }
  const min = Math.min(0, ...values)
  const max = Math.max(0, ...values)
  const range = max - min || 1
  const y = (v: number) => 2 + (h - 4) - ((v - min) / range) * (h - 4)
  const step = w / (values.length - 1)
  const pts = values.map((v, i) => [i * step, y(v)] as const)
  const line = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`)
    .join(' ')
  const up = values[values.length - 1] >= 0
  const color = up ? 'var(--pulse)' : 'var(--rose)'
  const fill = up ? 'var(--pulse-faint)' : 'var(--rose-faint)'
  const area = `${line} L${w},${y(0).toFixed(1)} L0,${y(0).toFixed(1)} Z`
  const last = pts[pts.length - 1]
  return (
    <svg width={w} height={h} className="block" aria-hidden>
      {min < 0 && max > 0 && (
        <line x1="0" x2={w} y1={y(0)} y2={y(0)}
          stroke="var(--line-hi)" strokeDasharray="2,3" strokeWidth="1" />
      )}
      <path d={area} fill={fill} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.6" />
      <circle cx={last[0]} cy={last[1]} r="2" fill={color} />
    </svg>
  )
}

// ─── Кумулятив дневной серии для спарклайна ──────────────────────────

function cumulative(daily: Array<{ d: string; pp: number }>): number[] {
  let acc = 0
  return (daily ?? []).map((x) => (acc += x.pp))
}

// ─── WR микробар ─────────────────────────────────────────────────────

function Wr({ pct }: { pct: number }) {
  return (
    <span className="inline-flex items-center gap-1.5 justify-end font-mono">
      <span className="wrbar"><i className={pct >= 50 ? 'hi' : ''} style={{ width: `${pct}%` }} /></span>
      {pct}%
    </span>
  )
}

// ─── Battery-пилюля ──────────────────────────────────────────────────

function BatteryPill({ verdict, ex }: { verdict: string | null; ex: number | null }) {
  if (!verdict) return <span className="bpill bpill-none">—</span>
  const v = verdict.toUpperCase()
  const cls = v.startsWith('PASS_PARTIAL') ? 'bpill-part'
    : v.startsWith('PASS') ? 'bpill-pass'
      : v.startsWith('FAIL') ? 'bpill-fail' : 'bpill-none'
  const label = v.startsWith('PASS_PARTIAL') ? 'PART' : v.split('_')[0]
  return (
    <span className={cn('bpill', cls)}>
      {label}{ex != null ? ` ${fmtPp(Number(ex), 2)}` : ''}
    </span>
  )
}

// ─── Карточка аккаунта ───────────────────────────────────────────────

function AccountCard({ a }: { a: any }) {
  const meta = ACCOUNT_LABELS[a.user_id] ?? { name: `user ${a.user_id}` }
  const usd = a.usd != null ? Number(a.usd) : null
  const up = (usd ?? 0) >= 0
  const age = fmtAge(a.eq_age_sec)
  const wr = a.n > 0 ? Math.round((a.wins / a.n) * 100) : null
  return (
    <div className="bg-surface border border-line rounded-lg shadow-soft p-4 transition-colors hover:border-lineHi">
      <div className="flex items-center gap-2 mb-2.5">
        <span className="text-xs font-semibold text-textDim">{meta.name}</span>
        <span className={cn('pill font-sans uppercase tracking-wider text-2xs',
          meta.real ? 'bg-pulse-faint text-pulse' : 'bg-elevated text-textDim')}>
          {meta.real ? 'реал' : 'демо'}
        </span>
        <span className="ml-auto inline-flex items-center gap-1.5 text-2xs text-textFaint">
          {age ? (
            <span className="pill font-sans bg-amber-faint text-amber">устарело {age}</span>
          ) : (
            <>
              <span className="dot animate-pulse" style={{ background: 'var(--pulse)' }} />
              live
            </>
          )}
        </span>
      </div>
      <div className="font-mono text-2xl font-semibold tracking-tight">
        {a.eq_now != null ? `$${Number(a.eq_now).toFixed(2)}` : '—'}
      </div>
      <div className="flex items-center gap-2 mt-2">
        {usd != null ? (
          <span className={cn('chip', up ? 'chip-up' : 'chip-dn')}>
            {up ? '▲' : '▼'} {fmtUsd(usd)}
          </span>
        ) : (
          <span className="text-xs text-textFaint">нет сделок за окно</span>
        )}
        {a.pp != null && (
          <span className="font-mono text-2xs text-textFaint">{fmtPp(Number(a.pp))} pp</span>
        )}
      </div>
      <div className="flex gap-4 items-center text-2xs text-textDim mt-3 pt-3 border-t border-line">
        <span>сделок <b className="font-mono text-text">{a.n ?? 0}</b></span>
        {wr != null && <Wr pct={wr} />}
        {a.open_now > 0 && <span>откр. <b className="font-mono text-text">{a.open_now}</b></span>}
      </div>
    </div>
  )
}

// ─── Страница ────────────────────────────────────────────────────────

export default function Analytics() {
  const [hours, setHours] = useState(168)
  const [sortKey, setSortKey] = useState<SortKey>('pp')
  const [onlyLive, setOnlyLive] = useState(true)
  const { data, isLoading } = useAnalytics(hours)

  const strategies = useMemo(() => {
    let rows = (data?.strategies ?? []) as any[]
    if (onlyLive) rows = rows.filter((r) => r.live)
    return [...rows].sort((a, b) => {
      if (sortKey === 'last_trade') {
        return (b.last_trade ?? '').localeCompare(a.last_trade ?? '')
      }
      return (Number(b[sortKey]) || 0) - (Number(a[sortKey]) || 0)
    })
  }, [data, sortKey, onlyLive])

  const th = (key: SortKey, label: string) => (
    <th
      className={cn(
        'px-3 py-2.5 font-medium text-2xs tracking-wide cursor-pointer select-none whitespace-nowrap text-right',
        sortKey === key ? 'text-text' : 'text-textFaint hover:text-textDim',
      )}
      onClick={() => setSortKey(key)}
    >
      {label}{sortKey === key ? ' ↓' : ''}
    </th>
  )

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-[1280px]">
      <header className="flex items-center gap-4 flex-wrap">
        <h1 className="text-xl font-semibold tracking-tight">Аналитика</h1>

        <div className="flex bg-surface border border-line rounded-md p-0.5 gap-0.5">
          {PERIODS.map((p) => (
            <button key={p.hours}
              onClick={() => setHours(p.hours)}
              className={cn('px-3 py-1.5 text-xs font-medium rounded-sm transition-colors',
                hours === p.hours
                  ? 'bg-elevated text-text shadow-soft'
                  : 'text-textFaint hover:text-textDim')}
            >{p.label}</button>
          ))}
        </div>

        <label className="flex items-center gap-2 text-xs text-textDim cursor-pointer select-none">
          <input type="checkbox" className="sr-only" checked={onlyLive}
            onChange={(e) => setOnlyLive(e.target.checked)} />
          <span className={cn('relative w-[30px] h-[17px] rounded-full border transition-colors flex-none',
            onlyLive ? 'bg-pulse-faint border-pulse' : 'bg-elevated border-line')}>
            <span className={cn('absolute top-[2px] w-[11px] h-[11px] rounded-full transition-all',
              onlyLive ? 'right-[2px] bg-pulse' : 'left-[2px] bg-textFaint')} />
          </span>
          только активные
        </label>

        {isLoading && <span className="text-xs text-textFaint">загрузка…</span>}
      </header>

      {/* ── Аккаунты ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3.5">
        {(data?.accounts ?? []).map((a: any) => <AccountCard key={a.user_id} a={a} />)}
      </div>

      {/* ── Стратегии ── */}
      <div className="bg-surface border border-line rounded-lg shadow-soft overflow-hidden">
        <div className="flex items-baseline gap-3 px-4 py-3 border-b border-line">
          <h2 className="text-sm font-semibold">Стратегии</h2>
          <span className="text-2xs text-textFaint font-mono">
            {strategies.length} {onlyLive ? 'активных' : 'всего'}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line">
                <th className="px-4 py-2.5 font-medium text-2xs tracking-wide text-textFaint text-left">Стратегия</th>
                {th('n', 'Сделки')}
                <th className="px-3 py-2.5 font-medium text-2xs tracking-wide text-textFaint text-right">WR</th>
                {th('pp', 'PnL')}
                {th('avg_pp', 'ср/трейд')}
                <th className="px-3 py-2.5 font-medium text-2xs tracking-wide text-textFaint text-right">макс + / −</th>
                <th className="px-3 py-2.5 font-medium text-2xs tracking-wide text-textFaint text-left">Тренд</th>
                <th className="px-3 py-2.5 font-medium text-2xs tracking-wide text-textFaint text-right">Battery</th>
                {th('last_trade', 'Активность')}
              </tr>
            </thead>
            <tbody>
              {strategies.map((r: any) => {
                const wr = r.n > 0 ? Math.round((r.wins / r.n) * 100) : 0
                const up = Number(r.pp) >= 0
                const lock = parseLock(r.lock)
                return (
                  <tr key={r.id}
                    className={cn('border-b border-line/50 last:border-b-0 transition-colors hover:bg-elevated/60',
                      !r.live && 'opacity-50')}>
                    <td className="px-4 py-2">
                      <span className="flex items-center gap-2 font-semibold whitespace-nowrap max-w-[300px] overflow-hidden text-ellipsis">
                        <Link to={`/app/lab/${r.id}`} className="hover:text-fire transition-colors">
                          {r.name}
                        </Link>
                        <span className={cn('pill text-[9.5px] font-bold',
                          r.dir === 'long' ? 'bg-pulse-faint text-pulse' : 'bg-rose-faint text-rose')}>
                          {r.dir === 'long' ? 'LONG' : 'SHORT'}
                        </span>
                        {r.managed_by !== 'discovery' && (
                          <span className="pill font-sans text-[9.5px] uppercase tracking-wider bg-sky-faint text-sky">lab</span>
                        )}
                      </span>
                      <span className="flex items-center gap-2.5 mt-0.5 text-2xs text-textFaint">
                        {lock && (
                          <span className="inline-flex items-center gap-1">
                            <Lock size={10} className="opacity-70" />{lock}
                          </span>
                        )}
                        <span>счёт {(r.accounts || []).join(', ')}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{r.n}</td>
                    <td className="px-3 py-2 text-right"><Wr pct={wr} /></td>
                    <td className="px-3 py-2 text-right">
                      <span className={cn('font-mono font-semibold', up ? 'text-pulse' : 'text-rose')}>
                        {fmtPp(Number(r.pp))} pp
                      </span>
                      <span className="block text-2xs text-textFaint font-mono mt-0.5">
                        {fmtUsd(Number(r.usd))}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-textDim">{fmtPp(Number(r.avg_pp), 2)}</td>
                    <td className="px-3 py-2 text-right font-mono text-2xs text-textFaint">
                      {fmtPp(Number(r.best))} / {fmtPp(Number(r.worst))}
                    </td>
                    <td className="px-3 py-2"><Spark values={cumulative(r.daily)} /></td>
                    <td className="px-3 py-2 text-right">
                      <BatteryPill verdict={r.battery_verdict} ex={r.ex_best_week} />
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-2xs text-textFaint whitespace-nowrap">
                      {fmtRel(r.last_trade)}
                    </td>
                  </tr>
                )
              })}
              {!strategies.length && !isLoading && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-textFaint text-sm">
                  нет сделок за выбранный период
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
